import { UserProfile } from 'fitbeat.gym-core';
import React, { FunctionComponent, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import headerImageHome from '../../assets/images/header-images/home-workout-knee-to-elbow.jpg';
import headerImageStudio from '../../assets/images/header-images/studio-header.jpg';
import { hasAccessToAStudioGymSelector } from '../../auth/auth-selectors';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import { openMembershipPlans } from '../../payments';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { TrackingImageTile } from '../../Tracking/components/TrackingImageTile';
import { defaultScale, isWeb } from '../../utils';
import { Logger } from '../../utils/Logger';
import { initialiseWeightTargetSet } from '../../WeightTarget/actions';
import { styles as welcomeTileStyles } from './WelcomeTile';

interface IProps {
  t: (key: string, options?: { [option: string]: string }) => string;
  userProfile: UserProfile;
  hasAccessToAStudioGym: boolean;
  initializeAndOpenTrackingModal: () => void;
}

const DashboardHeaderSeeMembershipPlans: FunctionComponent<IProps> = (
  {
    t,
    userProfile,
    initializeAndOpenTrackingModal,
    hasAccessToAStudioGym,
  },
) => {
  const [openingMembershipPlans, setOpeningMembershipPlans] = useState(false);

  const setLoadingAndOpenMembershipPlans = () => {
    openMembershipPlans(userProfile)
      .catch((e) => Logger.notify(e));
    setOpeningMembershipPlans(true);
    setTimeout(() => setOpeningMembershipPlans(false), 3000);
  };

  const getGymDisplayName = (): string => {
    if (!userProfile.gymName) {
      return '';
    }
    try {
      const gym = GymResolver.getByName(userProfile.gymName);
      return gym.displayName;
    } catch {
      return '';
    }
  };

  const getGymHeader = (): string => {
    const gym = GymResolver.getByName(userProfile.gymName);
    return gym.personalisedHeader || gym.displayName;
  };

  return (
    <TrackingImageTile
      imageSource={hasAccessToAStudioGym ? headerImageStudio : headerImageHome}
      containerStyle={styles.headerImageContainer}
      titleStyle={welcomeTileStyles.welcomeTextHeader}
      title={
        hasAccessToAStudioGym
          ? t('dashboard:welcomeName', { name: userProfile.firstname })
          : t('payments:yourMembershipHasExpired')
      }
      useGradient
      contentContainerStyle={{ paddingBottom: 0 }}
      actions={
        <View style={{ justifyContent: 'center' }}>
          <Text style={welcomeTileStyles.welcomeTextMessage}>
            {hasAccessToAStudioGym
              ? t('dashboard:becomeGymMemberForReasons', { gymName: getGymDisplayName() })
              : t('payments:GymActivationSubHeading', { gymPersonalisedHeader: getGymHeader() })
            }
          </Text>
          <View>
              <Button
                title={t('dashboard:seeMembershipPlans')}
                fontColor={Theme.BLACK}
                onPress={setLoadingAndOpenMembershipPlans}
                containerStyle={{ marginBottom: defaultScale(10) }}
                overrideFontStyles={{ color: Theme.WHITE }}
                size={ButtonSize.MEDIUM}
                isLoading={openingMembershipPlans}
              />
            {!isWeb() && hasAccessToAStudioGym &&
              <Button
                title={t('dashboard:doABodyScan')}
                onPress={initializeAndOpenTrackingModal}
                buttonType={ButtonTypes.OUTLINE}
                overrideFontStyles={[ welcomeTileStyles.workoutButtonText, { color: Theme.PRIMARY }]}
                size={ButtonSize.MEDIUM}
              />
            }
          </View>
        </View>
      }
    />
  );
};

const styles = StyleSheet.create({
  headerImageContainer: {
    height: defaultScale(367),
    marginBottom: defaultScale(15),
  },
  welcomeTextMessage: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
    paddingHorizontal: 30,
    paddingTop: 10,
    paddingBottom: 25,
    textAlign: 'center',
  },
});

const mapDispatchToProps = (dispatch, ownProps: IProps) => ({
  initializeAndOpenTrackingModal: () => dispatch(initialiseWeightTargetSet(ownProps.userProfile)),
});

const mapStateToProps = (state: IAppStore, ownProps: IProps) => ({
  hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardHeaderSeeMembershipPlans);
